import { fmtBRL } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend } from "recharts";

const Tip = ({ active, payload, counts }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (counts) return (
    <div className="rounded-lg border border-[#1F293D] bg-[#0F141F] px-3 py-2 font-mono text-xs text-slate-200 shadow-xl">
      <div className="mb-1 font-sans font-semibold">{p.label} · {p.day}{p.is_today ? " · HOJE" : ""}</div>
      <div className="text-sky-300">Vencimentos: {p.forecast_count}</div>
      <div className="text-emerald-300">Pagamentos: {p.received_count}</div>
      <div className="mt-1 text-amber-300">Dos que venciam: {p.paid_due_count} pagaram{p.pay_rate != null ? ` (${p.pay_rate.toLocaleString("pt-BR")}%)` : ""}</div>
    </div>
  );
  return (
    <div className="rounded-lg border border-[#1F293D] bg-[#0F141F] px-3 py-2 font-mono text-xs text-slate-200 shadow-xl">
      <div className="mb-1 font-sans font-semibold">{p.label} · {p.day}{p.is_today ? " · HOJE" : ""}</div>
      <div className="text-sky-300">Previsto: {fmtBRL(p.forecast_amount)} ({p.forecast_count})</div>
      <div className="text-emerald-300">Recebido: {fmtBRL(p.received_amount)} ({p.received_count})</div>
    </div>
  );
};

const rateColor = (r) => (r >= 70 ? "#34D399" : r >= 40 ? "#FBBF24" : "#F87171");

const TodayRate = ({ d }) => {
  if (!d || !d.forecast_count) return <span className="text-xs text-slate-500">Segunda a domingo</span>;
  const left = d.forecast_count - d.paid_due_count;
  return (
    <div data-testid="today-pay-rate" className="text-right">
      <div className="font-mono text-lg font-extrabold" style={{ color: rateColor(d.pay_rate) }}>{(d.pay_rate || 0).toLocaleString("pt-BR")}% pagaram hoje</div>
      <div className="text-[11px] text-slate-400">{d.paid_due_count} de {d.forecast_count} · <b className="text-slate-200">faltam {left}</b></div>
    </div>
  );
};

export const WeekChart = ({ days, counts = false }) => (
  <div data-testid="section-semana-corrente" className="lg:col-span-8 rounded-2xl border border-[#1F293D] bg-[#111827] p-5 fade-up" style={{ animationDelay: "120ms" }}>
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-200">{counts ? "Vencimentos × pagamentos" : "Previsto × recebido por dia"}</h3>
      {counts ? <TodayRate d={days.find((x) => x.is_today)} /> : <span className="text-xs text-slate-500">Segunda a domingo</span>}
    </div>
    <div data-testid="chart-semana-dias">
      <ResponsiveContainer width="100%" height={270}>
        <BarChart data={days} margin={{ left: -10, right: 6, top: 10 }} barGap={4}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1F293D" vertical={false} />
          <XAxis dataKey="label" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false}
            tick={({ x, y, payload, index }) => (
              <g>
                <text x={x} y={y + 12} textAnchor="middle" fontSize={12} fontWeight={days[index]?.is_today ? 800 : 500}
                  fill={days[index]?.is_today ? "#FBBF24" : "#64748B"}>{days[index]?.is_today ? "HOJE" : payload.value}</text>
                {counts && days[index]?.pay_rate != null && days[index].date <= (days.find((x) => x.is_today)?.date || "") && (
                  <text x={x} y={y + 27} textAnchor="middle" fontSize={11} fontWeight={700} fill={rateColor(days[index].pay_rate)}>{`${Math.round(days[index].pay_rate)}% pagos`}</text>
                )}
              </g>
            )} height={counts ? 40 : 30} />
          <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={!counts} tickFormatter={(v) => (counts ? v : `R$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`)} />
          <Tooltip content={<Tip counts={counts} />} cursor={{ fill: "#1A2234" }} />
          <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
          <Bar dataKey={counts ? "forecast_count" : "forecast_amount"} name={counts ? "Vencimentos" : "Previsto"} fill="#0369A1" radius={[6, 6, 0, 0]}>
            {days.map((d) => <Cell key={d.date} fill={d.is_today ? "#38BDF8" : "#0369A1"} />)}
          </Bar>
          <Bar dataKey={counts ? "received_count" : "received_amount"} name={counts ? "Pagamentos" : "Recebido"} fill="#34D399" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);
